import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Upload, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const AUDIENCE_LABELS = { student: "Élèves", parent: "Parents", teacher: "Enseignants", staff: "Personnel" };

const fileToB64 = (f) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(f);
});

// ---------- Dialog : ajouter un document ----------
export const AddItemDialog = ({ open, onOpenChange, meta, onCreated }) => {
  const EMPTY = { title: "", author: "", category: "Manuel scolaire", subject: "", description: "", copies_total: 0 };
  const [form, setForm] = useState(EMPTY);
  const [levels, setLevels] = useState([]);
  const [audience, setAudience] = useState(["student", "parent", "teacher", "staff"]);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Titre obligatoire");
    if (audience.length === 0) return toast.error("Choisissez au moins un public");
    setSaving(true);
    try {
      let file_data_b64 = null, file_name = "";
      if (file) {
        if (file.size > 25_000_000) throw new Error("Fichier trop volumineux (max 25 Mo)");
        file_data_b64 = await fileToB64(file);
        file_name = file.name;
      }
      await api.post("/library/items", { ...form, copies_total: +form.copies_total || 0, levels, audience, file_data_b64, file_name });
      toast.success("Document ajouté au catalogue");
      setForm(EMPTY); setLevels([]); setFile(null);
      onOpenChange(false);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-item-dialog">
        <DialogHeader><DialogTitle>Nouveau document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Titre *</Label>
            <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Mathématiques CE2 — Livre de l'élève" data-testid="item-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Auteur / Éditeur</Label>
              <Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} data-testid="item-author" />
            </div>
            <div>
              <Label>Matière</Label>
              <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Mathématiques" data-testid="item-subject" />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="item-category"><SelectValue /></SelectTrigger>
                <SelectContent>{meta.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Exemplaires physiques</Label>
              <Input type="number" min="0" value={form.copies_total} onChange={e => setForm({ ...form, copies_total: e.target.value })} data-testid="item-copies" />
            </div>
          </div>
          <div>
            <Label>Niveaux (curriculum) — vide = tous</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {meta.levels.map(l => (
                <button key={l} type="button" onClick={() => toggle(levels, setLevels, l)}
                  className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition ${levels.includes(l) ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}
                  data-testid={`level-chip-${l}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>Public autorisé</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {Object.entries(AUDIENCE_LABELS).map(([k, lab]) => (
                <button key={k} type="button" onClick={() => toggle(audience, setAudience, k)}
                  className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition ${audience.includes(k) ? "bg-[#166534] text-white border-[#166534]" : "bg-white text-slate-600 border-slate-200"}`}
                  data-testid={`audience-chip-${k}`}>{lab}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="item-description" />
          </div>
          <div>
            <Label>Fichier numérique (PDF, EPUB, image — max 25 Mo)</Label>
            <input ref={fileRef} type="file" accept=".pdf,.epub,.png,.jpg,.jpeg" className="hidden"
              onChange={e => setFile(e.target.files?.[0] || null)} data-testid="item-file-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="w-full gap-2 mt-1" data-testid="item-file-btn">
              {file ? <><CheckCircle2 className="h-4 w-4 text-green-600" />{file.name}</> : <><Upload className="h-4 w-4" />Choisir un fichier (optionnel)</>}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="item-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Statut d'emprunt ----------
const LOAN_BADGES = {
  en_cours: ["En cours", "bg-blue-100 text-blue-700"],
  en_retard: ["En retard", "bg-red-100 text-red-700"],
  rendu: ["Rendu", "bg-green-100 text-green-700"],
};
export const LoanBadge = ({ s }) => {
  const [label, cls] = LOAN_BADGES[s] || [s, "bg-slate-100 text-slate-500"];
  return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

// ---------- Onglet Emprunts (gestion) ----------
export const LoansTab = ({ items, onChanged }) => {
  const [loans, setLoans] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ item_id: "", matricule: "", due_date: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/library/loans"); setLoans(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.item_id || !form.matricule.trim() || !form.due_date) return toast.error("Tous les champs sont obligatoires");
    setSaving(true);
    try {
      await api.post("/library/loans", form);
      toast.success("Emprunt enregistré");
      setDlg(false); setForm({ item_id: "", matricule: "", due_date: "" });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const ret = async (l) => {
    try { await api.post(`/library/loans/${l.id}/return`); toast.success("Exemplaire rendu"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const lendable = items.filter(i => i.copies_total > 0);

  return (
    <div className="space-y-4" data-testid="loans-tab">
      <Button onClick={() => setDlg(true)} className="bg-[#1E3A8A] text-white gap-1" data-testid="add-loan-btn">
        <Plus className="h-4 w-4" />Nouvel emprunt
      </Button>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2 text-left">Ouvrage</th>
              <th className="px-4 py-2 text-left">Élève</th>
              <th className="px-4 py-2 text-left">À rendre le</th>
              <th className="px-4 py-2 text-left">Statut</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {loans.map(l => (
              <tr key={l.id} className={`border-t border-slate-100 ${l.status === "en_retard" ? "bg-red-50" : ""}`} data-testid={`loan-${l.id}`}>
                <td className="px-4 py-2 font-semibold">{l.item_title}</td>
                <td className="px-4 py-2">{l.student_name}<span className="text-xs text-slate-400 ml-1">{l.class_name} · {l.matricule}</span></td>
                <td className="px-4 py-2 font-mono text-xs">{l.due_date}</td>
                <td className="px-4 py-2"><LoanBadge s={l.status} /></td>
                <td className="px-4 py-2 text-right">
                  {!l.returned_at && (
                    <Button size="sm" variant="outline" onClick={() => ret(l)} className="h-7 text-xs text-[#166534] border-green-200" data-testid={`return-${l.id}`}>
                      Marquer rendu
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {loans.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Aucun emprunt enregistré.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="loan-dialog">
          <DialogHeader><DialogTitle>Nouvel emprunt</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ouvrage *</Label>
              <Select value={form.item_id} onValueChange={v => setForm({ ...form, item_id: v })}>
                <SelectTrigger data-testid="loan-item"><SelectValue placeholder="Choisir un ouvrage" /></SelectTrigger>
                <SelectContent>
                  {lendable.map(i => <SelectItem key={i.id} value={i.id}>{i.title} ({i.copies_available} dispo)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Matricule de l&apos;élève *</Label>
              <Input value={form.matricule} onChange={e => setForm({ ...form, matricule: e.target.value })} placeholder="MZD-26-00001" className="font-mono" data-testid="loan-matricule" />
            </div>
            <div>
              <Label>Date de retour prévue *</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="loan-due-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="loan-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Mes emprunts (familles) ----------
export const MyLoans = () => {
  const [loans, setLoans] = useState([]);
  useEffect(() => { api.get("/library/loans/my").then(({ data }) => setLoans(data)).catch(() => {}); }, []);
  if (loans.length === 0) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="my-loans">
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Mes emprunts</div>
      <div className="divide-y divide-slate-100">
        {loans.map(l => (
          <div key={l.id} className="px-5 py-2.5 flex items-center justify-between gap-2 flex-wrap" data-testid={`my-loan-${l.id}`}>
            <div>
              <div className="font-semibold text-sm text-slate-800">{l.item_title}</div>
              <div className="text-xs text-slate-500">{l.student_name} · à rendre le {l.due_date}</div>
            </div>
            <LoanBadge s={l.status} />
          </div>
        ))}
      </div>
    </div>
  );
};
